import { DateTime } from "luxon";
import { useMemo } from "react";
import { useGetList } from "react-admin";
import { useQuery } from "react-query";
import { DrivingSchool } from "../../providers/drivingSchoolsProvider";
import { Student, studentsProvider } from "../../providers/studentsProvider";
import { currentUserRoles } from "../../backoffice.access_control";

export const DUNNING_RESOURCES = [
  "candidatesForBlocking",
  "candidatesForFirstDunningNotice",
  "candidatesForSecondDunningNotice",
  "candidatesForHandoverToPairFinance",
  "studentsHandedOverToPairFinance",
] as const;

export type DunningResource = (typeof DUNNING_RESOURCES)[number];

export type DunningStudent = Student & { lastStepDuration: number | null };

export type DunningDashboard = {
  studentsByResource: Record<DunningResource, DunningStudent[]>;
  amountByResource: Record<DunningResource, number>;
  totalAmount: number;
};

export const DUNNING_DRIVING_SCHOOLS_FILTER = {
  customizations: (customizations: DrivingSchool["customizations"]) => customizations.includes("dunning"),
  isActive: (isActive: DrivingSchool["isActive"]) => isActive,
};

export function useDunningDashboard({
  drivingSchoolId,
  showPostponed,
  useGlobalCandidateQueries,
}: {
  drivingSchoolId?: string;
  showPostponed: boolean;
  useGlobalCandidateQueries: boolean;
}): {
  dashboard: DunningDashboard | undefined;
  drivingSchoolsById: Record<string, DrivingSchool>;
  isLoading: boolean;
} {
  const { data: dunningDrivingSchools, isLoading: areDunningDrivingSchoolsLoading } = useGetList<DrivingSchool>(
    "drivingSchools",
    {
      filter: DUNNING_DRIVING_SCHOOLS_FILTER,
      pagination: { page: 1, perPage: 9999 },
      sort: { field: "name", order: "ASC" },
    },
  );
  const dunningDrivingSchoolIds = dunningDrivingSchools?.map((drivingSchool) => drivingSchool.id);
  const drivingSchoolsById = useMemo(
    () => Object.fromEntries((dunningDrivingSchools ?? []).map((drivingSchool) => [drivingSchool.id, drivingSchool])),
    [dunningDrivingSchools],
  );
  const shouldLoadStudents = dunningDrivingSchoolIds !== undefined;
  const hasDunningDrivingSchools = (dunningDrivingSchoolIds?.length ?? 0) > 0;
  const shouldUseGlobalCandidateQueries = useGlobalCandidateQueries && currentUserRoles.includes("admin");
  const { data: candidateStudents = [], isLoading: areCandidateStudentsLoading } = useQuery(
    ["dunning", "candidateStudents", dunningDrivingSchoolIds],
    () => studentsProvider.getDunningCandidateStudents(new Set(dunningDrivingSchoolIds ?? [])),
    { enabled: hasDunningDrivingSchools && shouldUseGlobalCandidateQueries },
  );
  const { data: students = [], isLoading: areStudentsLoading } = useGetList<Student>(
    "students",
    {
      filter: drivingSchoolId ? { drivingSchoolId } : { drivingSchoolIds: dunningDrivingSchoolIds ?? [] },
      pagination: { page: 1, perPage: 9999 },
      sort: { field: "id", order: "ASC" },
    },
    { enabled: shouldLoadStudents && !shouldUseGlobalCandidateQueries },
  );
  const studentsForDashboard = useMemo(() => {
    const permittedDrivingSchoolIds = new Set(drivingSchoolId ? [drivingSchoolId] : dunningDrivingSchoolIds);
    const loadedStudents = shouldUseGlobalCandidateQueries ? candidateStudents : students;
    return loadedStudents.filter((student) => permittedDrivingSchoolIds.has(student.drivingSchoolId));
  }, [candidateStudents, drivingSchoolId, dunningDrivingSchoolIds, shouldUseGlobalCandidateQueries, students]);
  const dashboard = useMemo(
    () =>
      shouldLoadStudents
        ? buildDunningDashboard(studentsForDashboard, { showPostponed, now: DateTime.now() })
        : undefined,
    [shouldLoadStudents, showPostponed, studentsForDashboard],
  );
  return {
    dashboard,
    drivingSchoolsById,
    isLoading:
      areDunningDrivingSchoolsLoading ||
      (shouldUseGlobalCandidateQueries ? areCandidateStudentsLoading : areStudentsLoading),
  };
}

export function buildDunningDashboard(
  students: Student[],
  { showPostponed, now }: { showPostponed: boolean; now: DateTime },
): DunningDashboard {
  const studentsByResource: Record<DunningResource, DunningStudent[]> = {
    candidatesForBlocking: [],
    candidatesForFirstDunningNotice: [],
    candidatesForSecondDunningNotice: [],
    candidatesForHandoverToPairFinance: [],
    studentsHandedOverToPairFinance: [],
  };
  for (const student of students) {
    const resource = getDunningResource(student, now);
    if (!resource || isDunningHidden(student, now, showPostponed)) {
      continue;
    }
    studentsByResource[resource].push({
      ...student,
      lastStepDuration: getDunningLastStepDuration(resource, student, now),
    });
  }
  for (const resource of DUNNING_RESOURCES) {
    studentsByResource[resource]
      .sort((student1, student2) => getResourceSortValue(resource, student1) - getResourceSortValue(resource, student2))
      .sort((student1, student2) => (student2.lastStepDuration ?? 0) - (student1.lastStepDuration ?? 0));
  }
  const amountByResource = Object.fromEntries(
    DUNNING_RESOURCES.map((resource) => [
      resource,
      studentsByResource[resource].reduce((amount, student) => amount - student.balance, 0),
    ]),
  ) as Record<DunningResource, number>;
  return {
    studentsByResource,
    amountByResource,
    totalAmount: DUNNING_RESOURCES.reduce((amount, resource) => amount + amountByResource[resource], 0),
  };
}

export function buildDunningDisabledFilter() {
  return {
    dunningDisabled: (dunningDisabled: boolean) => !dunningDisabled,
  };
}

export function buildPostponedFilter(now: DateTime) {
  return {
    dunningPostponedUntil: (dunningPostponedUntil: Student["dunningPostponedUntil"]) =>
      !dunningPostponedUntil || dunningPostponedUntil <= now,
  };
}

function getDunningResource(student: Student, now: DateTime): DunningResource | undefined {
  const { dunningProcess } = student;
  if (student.paymentStrategy === "purchaseOnAccount") {
    return;
  }
  if (
    student.balance < 0 &&
    student.status !== "outstandingPayments" &&
    !dunningProcess &&
    !(student.paymentStrategy === "upfrontPayment" && student.startDate && now.diff(student.startDate, "days").days < 7)
  ) {
    return "candidatesForBlocking";
  }
  if (
    student.status === "outstandingPayments" &&
    student.balance < 0 &&
    (!dunningProcess || dunningProcess.status === "warningSent" || dunningProcess.status === "paymentReminderSent")
  ) {
    return "candidatesForFirstDunningNotice";
  }
  if (
    student.balance < 0 &&
    dunningProcess &&
    dunningProcess.outstandingAmount > 0 &&
    dunningProcess.status === "firstDunningNoticeSent"
  ) {
    return "candidatesForSecondDunningNotice";
  }
  if (
    student.balance < 0 &&
    dunningProcess &&
    dunningProcess.outstandingAmount > 0 &&
    dunningProcess.status === "secondDunningNoticeSent"
  ) {
    return "candidatesForHandoverToPairFinance";
  }
  if (dunningProcess?.status === "handedOverToPairFinance") {
    return "studentsHandedOverToPairFinance";
  }
}

function isDunningHidden(student: Student, now: DateTime, showPostponed: boolean): boolean {
  return (
    student.dunningDisabled ||
    (!!student.dunningPostponedUntil && student.dunningPostponedUntil > now) ||
    (!showPostponed && !!student.dunningProcess?.postponedUntil && student.dunningProcess.postponedUntil > now)
  );
}

function getResourceSortValue(resource: DunningResource, student: Student): number {
  if (resource === "studentsHandedOverToPairFinance") {
    return -(student.dunningProcess?.outstandingAmount ?? 0);
  }
  return student.balance;
}

function getDunningLastStepDuration(resource: DunningResource, student: Student, now: DateTime): number | null {
  const { dunningProcess } = student;
  let stepDate: DateTime | undefined | null = null;
  if (resource === "candidatesForFirstDunningNotice") {
    stepDate = dunningProcess?.paymentReminderSentAt;
  } else if (resource === "candidatesForSecondDunningNotice") {
    stepDate = dunningProcess?.firstDunningNoticeSentAt;
  } else if (resource === "candidatesForHandoverToPairFinance") {
    stepDate = dunningProcess?.secondDunningNoticeSentAt;
  } else if (resource === "studentsHandedOverToPairFinance") {
    stepDate = dunningProcess?.handedOverToPairFinanceAt;
  }
  return stepDate ? Math.floor(now.diff(stepDate, "days").days) : null;
}
